import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

const STORAGE_KEY = "vb_ai_sidebar_state_v1";

const AIAssistantContext = createContext(null);

const safeParseJson = (s) => {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
};

const clampMessages = (msgs, max = 40) => {
  const arr = Array.isArray(msgs) ? msgs : [];
  if (arr.length <= max) return arr;
  return arr.slice(arr.length - max);
};

export function AIAssistantProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [previousResponseId, setPreviousResponseId] = useState(null);
  const [proposedPatch, setProposedPatch] = useState(null);
  const [prefill, setPrefill] = useState(null);

  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    if (typeof window === "undefined") return;
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    const parsed = raw ? safeParseJson(raw) : null;
    if (!parsed || typeof parsed !== "object") return;

    const nextMessages = clampMessages(parsed.messages, 40).map((m) => ({
      role: m?.role === "user" ? "user" : "assistant",
      content: String(m?.content ?? ""),
    }));
    setMessages(nextMessages.length ? nextMessages : []);
    setPreviousResponseId(typeof parsed.previousResponseId === "string" ? parsed.previousResponseId : null);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!hydratedRef.current) return;
    try {
      window.localStorage?.setItem(
        STORAGE_KEY,
        JSON.stringify({
          messages: clampMessages(messages, 40).map((m) => ({ role: m.role, content: m.content })),
          previousResponseId,
        })
      );
    } catch {
      // ignore
    }
  }, [messages, previousResponseId]);

  const api = useMemo(
    () => ({
      open,
      setOpen,
      toggle: () => setOpen((v) => !v),
      prefill,
      consumePrefill: () => setPrefill(null),
      openWithPrefill: (text) => {
        const t = typeof text === "string" ? text : "";
        if (t.trim()) setPrefill(t);
        setOpen(true);
      },
      messages,
      setMessages,
      previousResponseId,
      setPreviousResponseId,
      proposedPatch,
      setProposedPatch,
      resetThread: () => {
        setMessages([]);
        setPreviousResponseId(null);
        setProposedPatch(null);
        setPrefill(null);
      },
    }),
    [open, messages, previousResponseId, proposedPatch, prefill]
  );

  return <AIAssistantContext.Provider value={api}>{children}</AIAssistantContext.Provider>;
}

AIAssistantProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAIAssistant() {
  const ctx = useContext(AIAssistantContext);
  if (!ctx) throw new Error("useAIAssistant must be used inside AIAssistantProvider");
  return ctx;
}

